import { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { Description as ReportIcon } from '@mui/icons-material';
import { ReportGeneratorProps, ReportData, DEFAULT_SECTIONS } from './types';
import { ProfessionalReportPreview } from './ProfessionalReportPreview';
import { buildSupplierPricingTotals } from '../pricing';
import { StepsLoadError } from '../EngagementSteps';
import { useEngagementConfiguration } from '../../../../store/AdviseEngagementConfiguration';
import { useSelectedMatrixFeatures } from '../../../../Api/Advise/useSelectedMatrixFeatures';
import { buildFeatureComparison } from './featureComparison';
import { LoadError } from '../LoadError';

// TODO(UPDEV-7335): report generation/preview and template persistence run entirely client-side.
// The server-side Advise report endpoints don't exist yet; re-wire an API client + store once they land.
export const ReportGenerator = ({ engagement, suppliers, customer, open, onClose }: ReportGeneratorProps) => {
  // Loaded here rather than passed in, so no page can build a report that leaves turned-off steps in.
  const { configuration, failed, retry } = useEngagementConfiguration(engagement.id);
  const matrix = useSelectedMatrixFeatures(engagement.id);
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);

  const selectedSuppliers = useMemo(
    () => suppliers.filter((v) => engagement.selected_suppliers?.includes(v.id)),
    [suppliers, engagement.selected_suppliers]
  );

  const handleGenerate = useCallback(() => {
    if (!configuration || !matrix.features) return;
    setIsGenerating(true);

    const today = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const featureComparison = buildFeatureComparison(matrix.features, selectedSuppliers, engagement.feature_notes);
    const detailedPricing = buildSupplierPricingTotals(engagement, selectedSuppliers);

    const fallbackSummary =
      'This report presents a comprehensive evaluation' +
      ` of supplier solutions for ${customer?.company_name || 'the customer'}.` +
      ` Based on detailed analysis of ${selectedSuppliers.length}` +
      ` suppliers across ${matrix.features.length}` +
      ' key requirements, we have identified the optimal solution' +
      " for the organization's needs.";

    const data: ReportData = {
      engagement,
      suppliers: selectedSuppliers,
      customer,
      date: today,
      executiveSummary: executiveSummary.trim() || fallbackSummary,
      featureComparison,
      detailedPricing,
      roiAnalysis: engagement.roi_analysis || {},
      settings: {
        sections: {
          ...DEFAULT_SECTIONS,
          ...((engagement.report_settings?.sections as Record<string, boolean>) || {})
        }
      },
      steps: configuration.steps
    };

    setReportData(data);
    setIsGenerating(false);
  }, [engagement, configuration, matrix.features, selectedSuppliers, customer, executiveSummary]);

  const handleClose = useCallback(() => {
    setReportData(null);
    setExecutiveSummary('');
    onClose();
  }, [onClose]);

  if (reportData) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullScreen>
        <ProfessionalReportPreview
          reportData={reportData}
          onClose={handleClose}
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>
        <Box className="flex items-center gap-2">
          <ReportIcon />
          Generate Professional Report
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box className="flex flex-col gap-4 mt-2">
          <Alert severity="info">
            <Typography variant="body2">
              This will generate a professional PDF-ready report with feature comparison, pricing analysis, and ROI data
              for <strong>{engagement.engagement_name}</strong>.
            </Typography>
          </Alert>

          <Box className="bg-blue-50 border border-blue-200 rounded p-3">
            <Typography
              variant="body2"
              fontWeight={600}
              gutterBottom>
              Report includes:
            </Typography>
            <ul className="text-sm text-gray-700 space-y-1 ml-4">
              <li>Executive summary</li>
              <li>Feature comparison matrix</li>
              <li>Supplier rankings</li>
              <li>Investment analysis with pricing breakdown</li>
              <li>ROI analysis (if configured)</li>
            </ul>
          </Box>

          {failed && (
            <StepsLoadError
              consequence="the report cannot leave out the ones turned off"
              onRetry={retry}
            />
          )}

          {matrix.failed && (
            <LoadError
              subject="the Functionality Matrix"
              consequence="the report cannot list the selected features"
              onRetry={matrix.retry}
            />
          )}

          <TextField
            label="Executive Summary (optional)"
            placeholder={'Enter a custom executive summary or leave blank ' + 'for an auto-generated one...'}
            multiline
            rows={4}
            value={executiveSummary}
            onChange={(e) => setExecutiveSummary(e.target.value)}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={isGenerating || !configuration || !matrix.features}
          startIcon={isGenerating ? <CircularProgress size={16} /> : <ReportIcon />}>
          {isGenerating ? 'Generating...' : 'Generate Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
